import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, ListChecks, PieChart, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import PageLayout from "@/components/layout/PageLayout";
import { Toaster } from "@/components/ui/toaster";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionStatus } from "@/components/subscription/SubscriptionStatus";
import { TenderWorkflow } from "@/components/dashboard/TenderWorkflow";
import { TenderStats } from "@/components/dashboard/TenderStats";
import { TenderSubscriptions } from "@/components/dashboard/TenderSubscriptions";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { TenderCalendar } from "@/components/dashboard/TenderCalendar";
const Dashboard = () => {
  const {
    t
  } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch saved tenders for the current user
  const {
    data: tenders,
    isLoading: tendersLoading,
    refetch: refetchTenders
  } = useQuery({
    queryKey: ['owned-tenders'],
    queryFn: async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return [];
      const {
        data,
        error
      } = await supabase.from('saved_tenders').select('*').eq('user_id', user.id).order('created_at', {
        ascending: false
      });
      if (error) {
        console.error("Error fetching saved tenders:", error);
        throw error;
      }
      return data || [];
    }
  });
  useEffect(() => {
    const checkUser = async () => {
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login");
        }
      } catch (error: any) {
        console.error("Error in checkUser:", error);
        setError(error.message || "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };
    checkUser();
  }, [navigate]);
  const handleRefresh = () => {
    window.location.reload();
  };
  return <PageLayout title={t("welcome")} description={t("overview")} icon={<LayoutDashboard className="h-6 w-6" />}>
      <div className="container mx-auto py-8">
        {loading ? <DashboardSkeleton /> : error ? <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-2">
              {t("errorLoadingProfile")}
            </h2>
            <Button onClick={handleRefresh}>
              {t("common.tryAgain")}
            </Button>
          </div> : <div className="grid gap-6">
            {/* Calendar Section - Most important time-critical information on top */}
            <section>
              
              <TenderCalendar tenders={tenders || []} />
            </section>
            
            {/* Stats Section - Key metrics directly below calendar */}
            <section>
              
              <TenderStats />
            </section>
            
            {/* Main Dashboard Content - Two Column Layout */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Left Column - Tender Workflow (Takes up 2/3 of space) */}
              <div className="md:col-span-2 space-y-6">
                <section>
                  
                  <TenderWorkflow tenders={tenders || []} onStatusUpdate={refetchTenders} />
                </section>
              </div>
              
              {/* Right Column - Subscription, Tender Subscriptions and Quick Actions (Takes up 1/3 of space) */}
              <div className="space-y-6">
                <section>
                  <h2 className="text-lg font-semibold mb-4">{t("dashboardSections.subscription", "Abonnement")}</h2>
                  <SubscriptionStatus />
                </section>
                
                <section>
                  <h2 className="text-lg font-semibold mb-4">{t("dashboardSections.categories", "Kategorier")}</h2>
                  <TenderSubscriptions />
                </section>
                
                <section>
                  <h2 className="text-lg font-semibold mb-4">{t("dashboardSections.quickActions", "Hurtige handlinger")}</h2>
                  <QuickActions />
                </section>
              </div>
            </div>
          </div>}
      </div>
      <Toaster />
    </PageLayout>;
};

// Local DashboardSkeleton component (kept within this file to avoid conflicts)
const DashboardSkeleton = () => {
  return <div className="grid gap-6">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
            <CardDescription>Loading...</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Button variant="outline" className="w-full" disabled>
              Loading...
            </Button>
            <Button variant="outline" className="w-full" disabled>
              Loading...
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
            <CardDescription>Loading...</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-primary" />
              <p>Loading...</p>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <p>Loading...</p>
            </div>
            <div className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-primary" />
              <p>Loading...</p>
            </div>
            <div className="flex items-center gap-2">
              <LayoutDashboard className="h-5 w-5 text-primary" />
              <p>Loading...</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
            <CardDescription>Loading...</CardDescription>
          </CardHeader>
          <CardContent>
            <p>Loading...</p>
          </CardContent>
        </Card>
      </div>
      <section>
        <h2 className="text-2xl font-semibold mb-4">Loading...</h2>
        <Card>
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
            <CardDescription>Loading...</CardDescription>
          </CardHeader>
          <CardContent>
            <p>Loading...</p>
          </CardContent>
        </Card>
      </section>
    </div>;
};
export default Dashboard;